import { Box, Button, Stack, Typography, styled } from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { useDropzone } from 'react-dropzone';
import { useCallback, useRef } from 'react';
import { useConvergeSnackBar } from '../../contexts';
import { v4 as uuidv4 } from 'uuid';
import { IUploadFile } from './file-uploader';
import { UploadFolderName } from '@converge/graphql';
import config from '@converge/config';

interface FileUploadDropzoneProps {
  selectedFolder: string;
  disabled?: boolean;
  onFilesAdded: (files: IUploadFile[]) => void;
  onAutoExpand?: () => void;
  error?: boolean;
  existingFiles?: { name: string; folder: string }[];
}

const UploadWrapperContainer = styled(Box, {
  shouldForwardProp: (prop) =>
    !['isDragAccept', 'isDragReject', 'isFocused', 'error'].includes(
      prop as string
    ),
})<{
  isDragAccept: boolean;
  isDragReject: boolean;
  isFocused: boolean;
  disabled?: boolean;
  error?: boolean;
}>((props) => ({
  flex: '1',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '40px',
  borderWidth: '2px',
  borderRadius: '10px',
  borderColor: props.disabled
    ? '#E0E0E0'
    : props.error
    ? '#D32F2F'
    : props.isDragAccept
    ? '#59b959'
    : props.isDragReject
    ? '#D32F2F'
    : props.isFocused
    ? '#0d1a58'
    : '#C4C4C4',
  borderStyle: 'dashed',
  backgroundColor: props.disabled ? '#F5F5F5' : '#E7FDFB',
  color: props.disabled ? '#BDBDBD' : '#0d1957',
  outline: 'none',
  transition: 'border 0.24s ease-in-out',
  minHeight: '150px',
  height: '150px',
  pointerEvents: props.disabled ? 'none' : 'auto',
  opacity: props.disabled ? 0.6 : 1,
}));

export const ALLOWED_EXTENSIONS = [
  'txt',
  'pdf',
  'xls',
  'xlsx',
  'xlsm',
  'doc',
  'docx',
  'png',
  'jpg',
  'jpeg',
  'eml',
  'mime',
  'md',
  'msg',
];

const hasInvalidFileNameChars = (fileName: string): boolean => {
  const invalidChars = /[*?<>|\\/:]/;
  return invalidChars.test(fileName);
};

const hasValidExtension = (fileName: string): boolean => {
  const parts = fileName?.split('.');
  if (parts?.length === 1) {
    return true;
  }
  const extension = parts?.pop()?.toLowerCase();
  return extension ? ALLOWED_EXTENSIONS.includes(extension) : true;
};

const normalizeFolder = (folder: string) =>
  folder.toUpperCase().replace(/\s+/g, '_');

export const FileUploadDropzone: React.FC<FileUploadDropzoneProps> = ({
  selectedFolder,
  disabled = false,
  onFilesAdded,
  onAutoExpand,
  error = false,
  existingFiles = [],
}) => {
  const { notify } = useConvergeSnackBar();
  const existingFilesRef = useRef(existingFiles);
  existingFilesRef.current = existingFiles;
  const selectedFolderRef = useRef(selectedFolder);
  selectedFolderRef.current = selectedFolder;

  const processFiles = useCallback(
    (files: File[]) => {
      const currentFolder = selectedFolderRef.current;
      const existingNamesSet = new Set(
        existingFilesRef.current.map(
          (f) => `${f.name}|${normalizeFolder(f.folder)}`
        )
      );

      const invalidCharFiles: string[] = [];
      const invalidExtFiles: string[] = [];
      const duplicateFiles: string[] = [];
      const validFiles: File[] = [];
      files.forEach((file) => {
        if (hasInvalidFileNameChars(file.name)) {
          invalidCharFiles.push(file.name);
        } else if (!hasValidExtension(file?.name)) {
          invalidExtFiles.push(file.name);
        } else if (
          existingNamesSet.has(`${file.name}|${normalizeFolder(currentFolder)}`)
        ) {
          duplicateFiles.push(file.name);
        } else {
          validFiles.push(file);
        }
      });

      if (invalidCharFiles.length > 0 && invalidExtFiles.length > 0) {
        notify(
          `Invalid file name and extension. Please remove special characters such as *, ?, <, >, |, \\, /, :, " and ensure file type is one of: ${ALLOWED_EXTENSIONS.join(
            ', '
          )}`,
          {
            variant: 'error',
          }
        );
      } else if (invalidCharFiles.length > 0) {
        notify(
          `Invalid characters in file name. Please remove any special characters such as *, ?, <, >, |, \\, /, :, " before uploading.`,
          {
            variant: 'error',
          }
        );
      } else if (invalidExtFiles.length > 0) {
        notify(
          `Invalid file extension. Only the following file types are allowed: ${ALLOWED_EXTENSIONS.join(
            ', '
          )}`,
          {
            variant: 'error',
          }
        );
      }

      if (duplicateFiles.length > 0) {
        notify(
          `Duplicate file${
            duplicateFiles.length > 1 ? 's' : ''
          } skipped: ${duplicateFiles.join(
            ', '
          )}. A file with the same name has already been added.`,
          {
            variant: 'error',
          }
        );
      }

      if (validFiles.length === 0) {
        return [];
      }

      const newFiles: IUploadFile[] = validFiles.map((file) => ({
        id: 'new-attachment' + uuidv4(),
        file,
        folder: currentFolder as UploadFolderName,
        progress: 0,
      }));

      onFilesAdded(newFiles);

      if (onAutoExpand) {
        onAutoExpand();
      }

      return newFiles;
    },
    [onFilesAdded, onAutoExpand, notify]
  );

  const onDropHandle = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        processFiles(acceptedFiles);
      }
    },
    [processFiles]
  );

  const {
    getRootProps,
    getInputProps,
    open: openFileDialog,
    isDragAccept,
    isFocused,
    isDragReject,
  } = useDropzone({
    onDrop: onDropHandle,
    noClick: true,
    noKeyboard: true,
    multiple: true,
    maxSize: config.maxFileSizeInBytes,
    validator: (file) => {
      if (!hasValidExtension(file?.name)) {
        return {
          code: 'invalid-extension',
          message: `Invalid file extension. Only the following file types are allowed: ${ALLOWED_EXTENSIONS.join(
            ', '
          )}`,
        };
      }
      return null;
    },
    onDropRejected: (rejectedFiles) => {
      const firstError = rejectedFiles[0]?.errors[0];
      const errorMessage =
        firstError?.code === 'file-too-large'
          ? `File is larger than ${config.maxFileSizeInMegabytes} MB`
          : firstError?.message ?? 'Unknown error';
      notify(`Unable to upload file: ${errorMessage}`, { variant: 'error' });
    },
  });

  return (
    <UploadWrapperContainer
      className="file-upload-dropbox"
      disabled={disabled}
      error={error}
      {...getRootProps({ isDragAccept, isFocused, isDragReject })}
    >
      <input {...getInputProps()} data-testid="file-input" />
      <Stack
        justifyContent={'center'}
        flexDirection={'row'}
        alignItems={'center'}
        gap={2}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CloudUploadOutlinedIcon sx={{ fontSize: 24, mb: 1 }} />
          <Box className="drag-drop">DRAG & DROP HERE</Box>
          <Typography variant="body1" sx={{ mb: 2, mt: 2, color: 'black' }}>
            OR
          </Typography>
          <Button
            variant="outlined"
            className="note-select-file-btn"
            sx={{
              px: 3,
            }}
            onClick={() => openFileDialog()}
          >
            SELECT FILE
          </Button>
        </Box>
      </Stack>
      <Box sx={{ mt: 1, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          Accepted: {ALLOWED_EXTENSIONS.join(', ')}
        </Typography>
      </Box>
    </UploadWrapperContainer>
  );
};
